import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useMediaQuery } from 'shared/ui/useMediaQuery';
import AppFooter from './AppFooter';
import Header from './Header';
import Sidebar from './Sidebar';
import { buildNavigation, readSessionMenu } from './navigation';
import './shell.css';

const COLLAPSED_KEY = 'sre.sidebar.collapsed';

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Shell de navegación: la cabecera cruza todo el ancho y debajo van la barra
 * lateral y el contenido. El contenido es el que hace scroll, así los elementos
 * sticky de cada pantalla se pegan bajo la cabecera.
 */
const AppShell = ({ children }: { children: ReactNode }) => {
  const groups = useMemo(() => buildNavigation(readSessionMenu()), []);
  const narrow = useMediaQuery('(max-width: 768px)');
  const [collapsedPref, setCollapsedPref] = useState(readCollapsed);
  const collapsed = narrow || collapsedPref;

  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const contentRef = useRef<HTMLElement>(null);

  // Al entrar a otra pantalla desde el menú, se empieza desde arriba.
  useEffect(() => {
    if (navigationType === 'PUSH') {
      contentRef.current?.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);

  const toggleCollapsed = () => {
    const next = !collapsedPref;
    setCollapsedPref(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      // Sin almacenamiento, la preferencia vale solo para esta visita.
    }
  };

  return (
    <>
      <a className="app-skip" href="#app-content">
        Saltar al contenido
      </a>
      <div className={`app-shell${collapsed ? ' is-collapsed' : ''}`}>
        <Header collapsed={collapsed} canToggle={!narrow} onToggle={toggleCollapsed} />
        <div className="app-body">
          <Sidebar groups={groups} collapsed={collapsed} />
          <div className="app-main">
            <main id="app-content" ref={contentRef} className="app-content" tabIndex={-1}>
              <div className="app-page">{children}</div>
              <AppFooter />
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppShell;
